import { selMessage } from "@selector/shared";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Tool } from "@/graphql.ts";
import type { IAppState } from "@/redux/reducer.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import {
  CHECK_HAS_INTERNET_FAILURE,
  CHECK_HAS_INTERNET_REQUEST,
  CHECK_HAS_INTERNET_SUCCESS,
  LOAD_TOOLS_ADMIN_FAILURE,
  LOAD_TOOLS_ADMIN_REQUEST,
  LOAD_TOOLS_ADMIN_SUCCESS,
} from "../action-types.js";
import { createReducer, type ReduxDispatch } from "../redux-utils.js";

/**
 * State definition
 */
export interface IToolAdminState {
  tools: Tool[];
  isLoadingTools: boolean;
  hasInternetConnection: boolean;
  isCheckingInternet: boolean;
}

const initialState: IToolAdminState = {
  tools: [],
  isLoadingTools: false,
  isCheckingInternet: false,
  hasInternetConnection: false,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_TOOLS_ADMIN_REQUEST]: (state: IToolAdminState): IToolAdminState => {
    return { ...state, isLoadingTools: true };
  },

  [LOAD_TOOLS_ADMIN_SUCCESS]: (state: IToolAdminState, tools: Tool[]): IToolAdminState => {
    return { ...state, tools, isLoadingTools: false };
  },

  [LOAD_TOOLS_ADMIN_FAILURE]: (state: IToolAdminState): IToolAdminState => {
    return { ...state, isLoadingTools: false };
  },

  [CHECK_HAS_INTERNET_REQUEST]: (state: IToolAdminState): IToolAdminState => {
    return { ...state, hasInternetConnection: true };
  },

  [CHECK_HAS_INTERNET_SUCCESS]: (state: IToolAdminState, ok: boolean): IToolAdminState => {
    return { ...state, hasInternetConnection: ok };
  },

  [CHECK_HAS_INTERNET_FAILURE]: (state: IToolAdminState): IToolAdminState => {
    return { ...state, hasInternetConnection: false };
  },
});

/**
 * Action creators
 */

export function loadAllTools() {
  return (dispatch: ReduxDispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Tool[]> => {
    dispatch({ type: LOAD_TOOLS_ADMIN_REQUEST });

    return webApiClient
      .fetchAdminTools()
      .then((tools: Tool[]) => {
        dispatch({
          type: LOAD_TOOLS_ADMIN_SUCCESS,
          payload: tools,
        });
        return tools;
      })
      .catch((error) => {
        createErrorHandler(dispatch, {
          actionType: LOAD_TOOLS_ADMIN_FAILURE,
          alertTitle: "Fetching of Admin Tools Failed",
        })(error);
        return [];
      });
  };
}

export function checkInternet(url: string | null) {
  return (dispatch: ReduxDispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch({ type: CHECK_HAS_INTERNET_REQUEST });

    return webApiClient
      .hasInternetConnection(url)
      .then((hasConnection: boolean) => {
        if (url == null && !hasConnection) {
          notify.info({
            title: selMessage({ id: "admin.noInternet.infoTitle" }),
            children: selMessage({ id: "admin.noInternet.infoMessage" }),
          });
        }
        dispatch({
          type: CHECK_HAS_INTERNET_SUCCESS,
          payload: hasConnection,
        });
        return hasConnection;
      })
      .catch((error) => {
        createErrorHandler(dispatch, {
          actionType: CHECK_HAS_INTERNET_FAILURE,
          alertTitle: "Checking for Internet Connection Failed",
        })(error);
        return false;
      });
  };
}
